import { css, html, LitElement, PropertyValues } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { query } from "lit/decorators/query.js";
import { property } from "lit/decorators/property.js";
import { COLOR_BLACK, HexColor, pickRandomItem, QUICK_COLORS } from "shared";
import { DEVICE_PIXEL_RATIO, EMOJI_FONT_NAME } from "../config";

const CONFETTI_SHAPES = ["circle", "square", "triangle", "emoji"] as const;
type ConfettiShape = (typeof CONFETTI_SHAPES)[number];

interface ConfettiConfig {
	shapes: readonly ConfettiShape[] | ConfettiShape[];
	maxSize: number;
	minSize: number;
	maxSpeed: number;
	minSpeed: number;
	maxRotationSpeed: number;
	minRotationSpeed: number;
	maxOpacitySpeed: number;
	minOpacitySpeed: number;
	maxOpacity: number;
	minOpacity: number;
	color?: HexColor | "random";
	emojis?: string[];
}

const defaultConfettiConfig: ConfettiConfig = {
	shapes: ["circle", "square", "triangle"],
	maxSize: 50,
	minSize: 10,
	maxSpeed: 4,
	minSpeed: 0.5,
	maxRotationSpeed: 0.01,
	minRotationSpeed: 0.001,
	maxOpacitySpeed: 0.01,
	minOpacitySpeed: 0.001,
	maxOpacity: 0.7,
	minOpacity: 0.1,
	color: COLOR_BLACK
};

interface Shape {
	type: ConfettiShape;
	x: number;
	y: number;
	size: number;
	dx: number;
	dy: number;
	rotation: number;
	rotationSpeed: number;
	opacity: number;
	opacityDirection: number;
	opacitySpeed: number;
	fill: HexColor;
	emoji?: string;
}

function getRandomNumberBetween(min: number, max: number, adjust: number = 0) {
	return (Math.random() + adjust) * (max - min + min);
}

const DEFAULT_EMOJIS = ["😀", "🚀", "💎"];

function createShape(
	config: ConfettiConfig,
	{ containerWidth, containerHeight }: { containerWidth: number; containerHeight: number }
): Shape {
	const type = pickRandomItem(config.shapes);
	return {
		type,
		x: getRandomNumberBetween(0, containerWidth),
		y: getRandomNumberBetween(0, containerHeight),
		size: getRandomNumberBetween(config.minSize, config.maxSize),
		dx: getRandomNumberBetween(config.minSpeed, config.maxSpeed, -0.5),
		dy: getRandomNumberBetween(config.minSpeed, config.maxSpeed, -0.5),
		rotationSpeed: getRandomNumberBetween(config.minRotationSpeed, config.maxRotationSpeed, -0.5),
		opacitySpeed: getRandomNumberBetween(config.minOpacitySpeed, config.maxOpacitySpeed, -0.5),
		opacity: getRandomNumberBetween(config.minOpacity, config.maxOpacity),
		rotation: Math.random() * Math.PI * 2,
		opacityDirection: Math.random() > 0.5 ? 1 : -1,
		fill: config.color === "random" ? pickRandomItem(QUICK_COLORS) : config.color ?? COLOR_BLACK,
		emoji: type === "emoji" ? pickRandomItem(config.emojis ?? DEFAULT_EMOJIS) : undefined
	};
}

/*
 * Thank you ChatGPT for the confetti code! <3
 */
@customElement("bs-confetti-wall")
export class BsConfettiWall extends LitElement {
	static styles = css`
		:host {
			display: block;
			position: relative;
			overflow: hidden;
		}

		#canvas {
			width: 100%;
			height: 100%;
		}
	`;

	@property({ type: Number }) count = 20;
	@property({ type: Object }) config?: Partial<ConfettiConfig>;

	@query("#canvas") $canvas!: HTMLCanvasElement;

	private ctx!: CanvasRenderingContext2D;
	private width = window.innerWidth;
	private height = window.innerHeight;
	private shapes: Shape[] = [];
	private currentAnimationId: number | null = null;

	private boundAnimateConfetti = this.animateConfetti.bind(this);
	private boundSetup = this.setup.bind(this);

	firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);
		window.addEventListener("resize", this.boundSetup);

		requestAnimationFrame(() => {
			this.setup();
			this.animateConfetti();
		});
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		window.removeEventListener("resize", this.boundSetup);
		this.stopAnimation();
	}

	stopAnimation() {
		if (this.currentAnimationId) {
			cancelAnimationFrame(this.currentAnimationId);
		}
	}

	setup() {
		this.width = this.offsetWidth;
		this.height = this.offsetHeight;
		this.$canvas.width = this.width * DEVICE_PIXEL_RATIO; // set canvas width in device pixels
		this.$canvas.height = this.height * DEVICE_PIXEL_RATIO; // set canvas height in device pixels
		this.ctx = this.$canvas.getContext("2d") as CanvasRenderingContext2D;
		this.ctx.scale(DEVICE_PIXEL_RATIO, DEVICE_PIXEL_RATIO); // scale context to match device pixel ratio
		this.shapes = this.createShapes(this.count);
	}

	createShapes(count: number): Shape[] {
		const shapes: Shape[] = [];

		const config = { ...defaultConfettiConfig, ...(this.config ?? {}) };
		for (let i = 0; i < count; i++) {
			shapes.push(createShape(config, { containerWidth: this.width, containerHeight: this.height }));
		}
		return shapes;
	}

	animateConfetti() {
		this.ctx.clearRect(0, 0, this.width, this.height);
		const config = { ...defaultConfettiConfig, ...(this.config ?? {}) };
		for (const shape of this.shapes) {
			this.ctx.fillStyle = shape.fill;
			this.ctx.globalAlpha = shape.opacity;
			this.ctx.save();
			this.ctx.translate(shape.x, shape.y);
			this.ctx.rotate(shape.rotation);

			if (shape.type === "circle") {
				this.ctx.beginPath();
				this.ctx.arc(0, 0, shape.size / 2, 0, Math.PI * 2);
				this.ctx.fill();
			} else if (shape.type === "square") {
				this.ctx.fillRect(-shape.size / 2, -shape.size / 2, shape.size, shape.size);
			} else if (shape.type === "triangle") {
				this.ctx.beginPath();
				this.ctx.moveTo(-shape.size / 2, shape.size / 2);
				this.ctx.lineTo(0, -shape.size / 2);
				this.ctx.lineTo(shape.size / 2, shape.size / 2);
				this.ctx.closePath();
				this.ctx.fill();
			} else if (shape.type === "emoji") {
				this.ctx.font = `${shape.size}px ${EMOJI_FONT_NAME}`;
				this.ctx.fillText(
					shape.emoji ?? pickRandomItem(this.config?.emojis ?? DEFAULT_EMOJIS),
					-shape.size / 2,
					shape.size / 2
				);
			}

			this.ctx.restore();
			shape.x += shape.dx;
			shape.y += shape.dy;
			shape.rotation += shape.rotationSpeed;
			shape.opacity += shape.opacitySpeed * shape.opacityDirection;

			if (shape.opacity < config.minOpacity || shape.opacity > config.maxOpacity) {
				shape.opacityDirection *= -1;
			}

			if (shape.x < 0 || shape.x > this.width) {
				shape.dx *= -1;
			}

			if (shape.y < 0 || shape.y > this.height) {
				shape.dy *= -1;
			}
		}

		this.currentAnimationId = requestAnimationFrame(this.boundAnimateConfetti);
	}

	render() {
		return html`<canvas id="canvas"></canvas>`;
	}
}
